import { useEffect, useRef, useState } from 'react';

/**
 * "Partner is typing" signal.
 *
 * The server relays `{ type: 'typing', userId, isTyping }` to the OTHER
 * participant only (see broadcastTyping in lib/ws.manager.ts), so no conversation
 * id needs matching here - receiving the event already means it is about the open
 * thread. A typing event has no "stopped" callback if the sender closes the tab,
 * so the flag self-clears after a short idle window.
 */
const TYPING_IDLE_MS = 6000;

export function usePartnerTyping({ socket, conversationId, currentUserId }) {
  const [typing, setTyping] = useState(false);
  const idleTimer = useRef(null);

  useEffect(() => {
    if (!socket) return undefined;

    return socket.subscribe((event) => {
      if (!event || event.type !== 'typing') return;
      if (!event.userId || event.userId === currentUserId) return;

      clearTimeout(idleTimer.current);
      setTyping(Boolean(event.isTyping));

      if (event.isTyping) {
        idleTimer.current = setTimeout(() => setTyping(false), TYPING_IDLE_MS);
      }
    });
  }, [socket, currentUserId]);

  // Switching threads must not carry the previous partner's signal over.
  useEffect(() => {
    clearTimeout(idleTimer.current);
    setTyping(false);
    return () => clearTimeout(idleTimer.current);
  }, [conversationId]);

  return typing;
}
